import { useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { useLang } from "@/context/LangContext";
import { Mic, Square, Upload, Loader2, Trash2, Sparkles, ChevronDown, ChevronUp, Save, FileText } from "lucide-react";

const ACCEPT = "audio/mpeg,audio/mp3,audio/mp4,audio/x-m4a,audio/m4a,audio/wav,audio/x-wav,audio/wave,audio/webm,audio/ogg,video/mp4,video/webm";
const MAX_MB = 25;

const fmt = (iso) => {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    return d.toLocaleString();
  } catch {
    return iso;
  }
};

/**
 * <VoiceNotes projectId="..." estimates={[...]} />
 *
 * Contractor-internal voice notes. Contractor can:
 *   1. Record audio in-browser via MediaRecorder
 *   2. Upload an audio file
 *   3. Transcribe via Whisper
 *   4. Review + edit the transcript
 *   5. Save the edit
 *   6. Append the transcript to a project estimate's notes field
 *   7. Delete the note
 *
 * Voice notes NEVER leak to customer-facing surfaces (proposals, contracts,
 * invoices, closeout packets, crew packets, public JSON, PDFs, emails).
 * They live in a separate ``voice_notes`` collection and are scrubbed via
 * ``customer_safety.FORBIDDEN_INTERNAL_FIELDS``.
 */
const VoiceNotes = ({ projectId, estimates = [] }) => {
  const { t } = useLang();
  const [notes, setNotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [recording, setRecording] = useState(false);
  const [recorder, setRecorder] = useState(null);
  const inputRef = useRef(null);
  const streamRef = useRef(null);
  const chunksRef = useRef([]);

  useEffect(() => {
    if (!projectId) return;
    setLoading(true);
    api
      .get(`/projects/${projectId}/voice-notes`)
      .then((r) => setNotes(r.data || []))
      .catch(() => setNotes([]))
      .finally(() => setLoading(false));
  }, [projectId]);

  const refresh = async () => {
    const r = await api.get(`/projects/${projectId}/voice-notes`);
    setNotes(r.data || []);
  };

  const upload = async (file, source) => {
    if (!file) return;
    if (file.size > MAX_MB * 1024 * 1024) {
      toast.error(t("voice_notes.exceeds_limit", MAX_MB));
      return;
    }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post(
        `/voice-notes/upload?project_id=${projectId}&source=${source}`,
        fd,
        { headers: { "Content-Type": "multipart/form-data" } }
      );
      toast.success(t("voice_notes.audio_uploaded"));
      // Auto-transcribe
      const { data: tr } = await api.post(`/voice-notes/${data.id}/transcribe`);
      toast.success(t("voice_notes.transcript_ready"));
      setNotes((prev) => [tr, ...prev]);
    } catch (err) {
      toast.error(err?.response?.data?.detail || t("voice_notes.upload_failed"));
      await refresh();
    } finally {
      setUploading(false);
    }
  };

  const onFilePick = (e) => {
    const f = e.target.files?.[0];
    if (f) upload(f, "uploaded");
    e.target.value = "";
  };

  const startRecording = async () => {
    if (recording) return;
    if (!navigator.mediaDevices?.getUserMedia) {
      toast.error(t("voice_notes.recording_unsupported"));
      return;
    }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;
      const mime = MediaRecorder.isTypeSupported("audio/webm") ? "audio/webm" : "";
      const mr = mime ? new MediaRecorder(stream, { mimeType: mime }) : new MediaRecorder(stream);
      chunksRef.current = [];
      mr.ondataavailable = (ev) => {
        if (ev.data && ev.data.size > 0) chunksRef.current.push(ev.data);
      };
      mr.onstop = async () => {
        const blob = new Blob(chunksRef.current, { type: mr.mimeType || "audio/webm" });
        // Stop mic
        try { streamRef.current?.getTracks().forEach((t) => t.stop()); } catch (_err) { /* noop */ }
        streamRef.current = null;
        const stamp = new Date().toISOString().replace(/[:.]/g, "-");
        // iter50 · Pick an extension that matches the recorded container so
        // the server MIME normalizer + Whisper both see a consistent file.
        const rawType = (mr.mimeType || "audio/webm").toLowerCase();
        const ext = rawType.includes("mp4") ? "mp4"
          : rawType.includes("ogg") ? "ogg"
          : rawType.includes("wav") ? "wav"
          : "webm";
        const file = new File([blob], `voice-note-${stamp}.${ext}`, { type: mr.mimeType || "audio/webm" });
        await upload(file, "recorded");
      };
      mr.start();
      setRecorder(mr);
      setRecording(true);
      toast.info(t("voice_notes.recording_prompt"));
    } catch (err) {
      toast.error(t("voice_notes.mic_denied"));
    }
  };

  const stopRecording = () => {
    if (!recording || !recorder) return;
    try { recorder.stop(); } catch (_err) { /* noop */ }
    setRecorder(null);
    setRecording(false);
  };

  const removeNote = async (id) => {
    if (!window.confirm(t("voice_notes.confirm_delete"))) return;
    try {
      await api.delete(`/voice-notes/${id}`);
      setNotes((prev) => prev.filter((n) => n.id !== id));
      toast.success(t("voice_notes.deleted"));
    } catch (err) {
      toast.error(err?.response?.data?.detail || t("voice_notes.delete_failed"));
    }
  };

  const saveEdit = async (id, newText) => {
    try {
      const { data } = await api.patch(`/voice-notes/${id}`, { transcript_edited: newText });
      setNotes((prev) => prev.map((n) => (n.id === id ? data : n)));
      toast.success(t("voice_notes.transcript_saved"));
    } catch (err) {
      toast.error(err?.response?.data?.detail || t("voice_notes.save_failed"));
    }
  };

  const retryTranscribe = async (id) => {
    try {
      const { data } = await api.post(`/voice-notes/${id}/transcribe`);
      setNotes((prev) => prev.map((n) => (n.id === id ? data : n)));
      toast.success(t("voice_notes.transcript_refreshed"));
    } catch (err) {
      toast.error(err?.response?.data?.detail || t("voice_notes.transcription_failed"));
    }
  };

  const appendToEstimate = async (note, estimateId) => {
    if (!estimateId) return;
    try {
      await api.post(`/voice-notes/${note.id}/append-to-estimate`, { estimate_id: estimateId });
      toast.success(t("voice_notes.appended_success"));
      // Reflect the (now-set) linked_estimate_id in local state
      setNotes((prev) => prev.map((n) => (n.id === note.id ? { ...n, linked_estimate_id: estimateId } : n)));
    } catch (err) {
      toast.error(err?.response?.data?.detail || t("voice_notes.append_failed"));
    }
  };

  return (
    <section className="jc-card p-6" data-testid="voice-notes-card">
      <div className="flex items-start justify-between gap-3 mb-4">
        <div>
          <div className="overline">{t("voice_notes.title")}</div>
          <p className="text-xs text-[var(--jc-text-soft)] mt-1 max-w-lg">
            {t("voice_notes.help")}
          </p>
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-2 mb-4">
        {!recording ? (
          <button
            type="button"
            onClick={startRecording}
            disabled={uploading}
            data-testid="voice-notes-record-btn"
            className="inline-flex items-center gap-1.5 px-3 py-2 text-sm font-semibold rounded-md jc-btn-primary disabled:opacity-60"
          >
            <Mic size={14} /> {t("voice_notes.record")}
          </button>
        ) : (
          <button
            type="button"
            onClick={stopRecording}
            data-testid="voice-notes-stop-btn"
            className="inline-flex items-center gap-1.5 px-3 py-2 text-sm font-semibold rounded-md bg-red-600 text-white hover:bg-red-700"
          >
            <Square size={14} /> {t("voice_notes.stop")}
          </button>
        )}
        <button
          type="button"
          onClick={() => inputRef.current?.click()}
          disabled={uploading || recording}
          data-testid="voice-notes-upload-btn"
          className="inline-flex items-center gap-1.5 px-3 py-2 text-sm font-semibold rounded-md border border-[var(--jc-border)] text-[var(--jc-text)] hover:bg-[var(--jc-surface-hover)] disabled:opacity-60"
        >
          {uploading ? <Loader2 className="animate-spin" size={14} /> : <Upload size={14} />}
          {t("voice_notes.upload_audio")}
        </button>
        <input
          ref={inputRef}
          type="file"
          accept={ACCEPT}
          onChange={onFilePick}
          className="hidden"
          data-testid="voice-notes-file-input"
        />
        <span className="text-xs text-[var(--jc-text-soft)]">{t("voice_notes.format_hint", MAX_MB)}</span>
      </div>

      {loading ? (
        <div className="text-sm text-[var(--jc-text-soft)]">{t("voice_notes.loading")}</div>
      ) : notes.length === 0 ? (
        <div className="text-sm text-[var(--jc-text-soft)]" data-testid="voice-notes-empty">
          {t("voice_notes.empty")}
        </div>
      ) : (
        <ul className="space-y-3" data-testid="voice-notes-list">
          {notes.map((n) => (
            <VoiceNoteRow
              key={n.id}
              note={n}
              estimates={estimates}
              onSaveEdit={saveEdit}
              onDelete={removeNote}
              onRetry={retryTranscribe}
              onAppend={appendToEstimate}
            />
          ))}
        </ul>
      )}
    </section>
  );
};

const VoiceNoteRow = ({ note, estimates, onSaveEdit, onDelete, onRetry, onAppend }) => {
  const { t } = useLang();
  const [open, setOpen] = useState(note.status !== "transcribed");
  const [draft, setDraft] = useState(note.transcript_edited || "");
  const [chosenEstimate, setChosenEstimate] = useState(
    note.linked_estimate_id || (estimates[0]?.id || "")
  );

  useEffect(() => {
    setDraft(note.transcript_edited || "");
  }, [note.transcript_edited, note.id]);

  const dirty = (note.transcript_edited || "") !== draft;
  const canAppend = !!chosenEstimate && (draft.trim().length > 0);
  const statusLabel = {
    uploaded: t("voice_notes.status_uploaded"),
    transcribing: t("voice_notes.status_transcribing"),
    transcribed: t("voice_notes.status_transcribed"),
    failed: t("voice_notes.status_failed"),
  }[note.status] || note.status;

  return (
    <li className="border border-[var(--jc-border)] rounded-md p-3" data-testid={`voice-note-row-${note.id}`}>
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="flex items-center gap-2 text-sm">
            <FileText size={14} className="text-[var(--jc-text-soft)] shrink-0" />
            <span className="font-semibold truncate">{note.original_filename || t("voice_notes.fallback_filename")}</span>
            <span
              className={`text-[10px] uppercase tracking-wide px-1.5 py-0.5 rounded ${
                note.status === "transcribed"
                  ? "bg-green-100 text-green-800"
                  : note.status === "failed"
                  ? "bg-red-100 text-red-800"
                  : "bg-yellow-100 text-yellow-800"
              }`}
              data-testid={`voice-note-status-${note.id}`}
            >
              {statusLabel}
            </span>
            {note.source === "recorded" && (
              <span className="text-[10px] uppercase tracking-wide px-1.5 py-0.5 rounded bg-[var(--jc-navy)]/10 text-[var(--jc-navy)]">
                {t("voice_notes.badge_recorded")}
              </span>
            )}
          </div>
          <div className="text-xs text-[var(--jc-text-soft)] mt-1">
            {fmt(note.created_at)} · {Math.round((note.size_bytes || 0) / 1024)} KB
            {note.linked_estimate_id ? t("voice_notes.linked_suffix") : ""}
          </div>
          {note.transcription_error && (
            <div className="text-xs text-red-700 mt-1">{t("voice_notes.error_prefix")} {note.transcription_error}</div>
          )}
        </div>
        <div className="flex items-center gap-1 shrink-0">
          <button
            type="button"
            onClick={() => setOpen((v) => !v)}
            data-testid={`voice-note-toggle-${note.id}`}
            className="p-1.5 rounded hover:bg-[var(--jc-surface-hover)]"
            aria-label={t("voice_notes.aria_toggle")}
          >
            {open ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
          </button>
          <button
            type="button"
            onClick={() => onDelete(note.id)}
            data-testid={`voice-note-delete-${note.id}`}
            className="p-1.5 rounded hover:bg-red-50 text-red-600"
            aria-label={t("voice_notes.aria_delete")}
          >
            <Trash2 size={16} />
          </button>
        </div>
      </div>

      {open && (
        <div className="mt-3 space-y-2">
          {note.status === "failed" || note.status === "uploaded" ? (
            <button
              type="button"
              onClick={() => onRetry(note.id)}
              data-testid={`voice-note-retry-${note.id}`}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold rounded-md jc-btn-primary"
            >
              <Sparkles size={12} /> {note.status === "failed" ? t("voice_notes.retry_transcription") : t("voice_notes.transcribe_now")}
            </button>
          ) : (
            <>
              <label className="text-xs text-[var(--jc-text-soft)]">{t("voice_notes.transcript_label")}</label>
              <textarea
                value={draft}
                onChange={(e) => setDraft(e.target.value)}
                rows={4}
                data-testid={`voice-note-transcript-${note.id}`}
                className="w-full text-sm rounded-md border border-[var(--jc-border)] px-3 py-2 font-mono"
                placeholder={t("voice_notes.transcript_placeholder")}
              />
              <div className="flex flex-wrap items-center gap-2">
                <button
                  type="button"
                  onClick={() => onSaveEdit(note.id, draft)}
                  disabled={!dirty}
                  data-testid={`voice-note-save-${note.id}`}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold rounded-md jc-btn-primary disabled:opacity-50"
                >
                  <Save size={12} /> {t("voice_notes.save_edit")}
                </button>
                <button
                  type="button"
                  onClick={() => onRetry(note.id)}
                  data-testid={`voice-note-refresh-${note.id}`}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold rounded-md border border-[var(--jc-border)] hover:bg-[var(--jc-surface-hover)]"
                  title={t("voice_notes.retranscribe_title")}
                >
                  <Sparkles size={12} /> {t("voice_notes.retranscribe")}
                </button>
                {estimates.length > 0 && (
                  <>
                    <select
                      value={chosenEstimate}
                      onChange={(e) => setChosenEstimate(e.target.value)}
                      data-testid={`voice-note-estimate-select-${note.id}`}
                      className="text-xs rounded-md border border-[var(--jc-border)] px-2 py-1.5"
                    >
                      {estimates.map((e) => (
                        <option key={e.id} value={e.id}>
                          {t("voice_notes.estimate_option", e.id.slice(0, 8), e.line_items?.length || 0)}
                        </option>
                      ))}
                    </select>
                    <button
                      type="button"
                      onClick={() => onAppend({ ...note, transcript_edited: draft }, chosenEstimate)}
                      disabled={!canAppend || dirty}
                      title={dirty ? t("voice_notes.dirty_title") : ""}
                      data-testid={`voice-note-append-${note.id}`}
                      className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold rounded-md border border-[var(--jc-border)] text-[var(--jc-blue)] hover:bg-[var(--jc-surface-hover)] disabled:opacity-50"
                    >
                      {t("voice_notes.append_to_estimate")}
                    </button>
                  </>
                )}
              </div>
              <p className="text-[11px] text-[var(--jc-text-soft)]">
                {t("voice_notes.internal_footer")}
              </p>
            </>
          )}
        </div>
      )}
    </li>
  );
};

export default VoiceNotes;
